import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useCallback, useMemo, useState } from 'react';
import { CircularProgress } from '@material-ui/core';
import { useAnalytics } from 'use-analytics';
import { FaRegCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import _, { isEmpty } from 'lodash';
import clsx from 'clsx';
import Dropdown from '../Dropdown';
import checkUserRole from '../../helpers/selectors/checkUserRole';
import {
  UserRoles, ResendEmailTypes, SearchStatus,
  BookingStatus,
  BookingAction,
} from '../../constants';
import {
  deleteTripRequest,
  resendEmailRequest, sendAgentCopyRequest, sendCancellationEmailRequest,
} from '../../store/slices/trip';
import LoginRequestModal from '../LoginRequestModal';
import formatTravelMode from '../../helpers/formatTravelMode';
import ConfirmModal from '../ConfirmModal';
import formatBookingStatus from '../../helpers/formatters/formatBookingStatus';
import DeleteConfirmationModal from '../ModalSpinner/DeleteConfirmationModal';
import ModalSpinner from '../ModalSpinner';
import useClipboard from '../../hooks/useClipboard';
import generateBrochureLink from '../../helpers/generateBrochureLink';

const navbarItemClasses = 'flex items-center whitespace-normal w-full px-2 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900 col-span-5 text-left capitalize';
const ResendEmailDropdown = ({ tripId, compareId, brochureId }) => {
  const dispatch = useDispatch();
  const { track } = useAnalytics();
  const isEndCustomer = useSelector(checkUserRole(UserRoles.CUSTOMER));
  const [doShowLoginModal, setDoShowLoginModal] = useState(false);
  const {
    isLoggedIn, email: loggedInUserEmail, travelAgency = {},
  } = useSelector((store) => store.user);
  const [tripToDelete, setTripToDelete] = useState(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const { deleteTripStatus, deleteTripError } = useSelector((store) => store.trip);
  const brochure = useSelector((state) => state.brochure.brochures[brochureId] || {});
  const [isLinkCopied, copyLinkToClipboard] = useClipboard();

  const {
    tripConfirmation,
    resendEmailStatus, sendClientCopyEmailError,
    sendAgentCopyStatus, sendAgentCopyEmailError,
    sendCancellationEmailStatus, cancellationEmailError,
  } = useSelector((store) => store.trip);

  const getSendCancellationEmailStatus = useCallback((tripSegmentId) => sendCancellationEmailStatus
    && sendCancellationEmailStatus[tripSegmentId], [sendCancellationEmailStatus]);

  const cancelledSegments = useMemo(() => tripConfirmation && tripConfirmation.bookingResponses
    && tripConfirmation.bookingResponses.filter((segment) => segment.bookingStatus
      && segment.bookingStatus === BookingStatus.cancelled)
      .map((segment) => ({
        tripSegmentId: segment.tripSegmentId, mode: segment.mode,
      })), [tripConfirmation]);

  const handleResendEmail = useCallback((actionType, tripSegmentId) => {
    if (!isLoggedIn) {
      setDoShowLoginModal(true);
      return;
    }
    if (
      actionType === ResendEmailTypes.SendClientCopy
      || actionType === ResendEmailTypes.SendBrochure
      || actionType === ResendEmailTypes.SendCompare
    ) {
      if (resendEmailStatus === SearchStatus.SEARCHING) return;
      dispatch(resendEmailRequest({ tripId, brochureId, compareId }));
    } else if (actionType === ResendEmailTypes.SendAgentCopy) {
      if (sendAgentCopyStatus === SearchStatus.SEARCHING) return;
      dispatch(sendAgentCopyRequest({ tripId }));
    } else if (actionType === ResendEmailTypes.SendCancallationEmail) {
      if (getSendCancellationEmailStatus(tripSegmentId) === SearchStatus.SEARCHING) return;
      dispatch(sendCancellationEmailRequest({ tripId, tripSegmentId }));
    }
    track('ResendEmail.handleEmailResend', {
      tripId: tripId ?? brochureId ?? compareId, loggedInUserEmail, actionType, tripSegmentId,
    });
  }, [track, tripId, loggedInUserEmail, resendEmailStatus, isLoggedIn, dispatch, brochureId,
    compareId, sendAgentCopyStatus, getSendCancellationEmailStatus]);

  const doRenderDropdown = (!isEndCustomer && travelAgency.doSendAgentCopy)
    || (cancelledSegments || []).length > 0 || brochureId;

  const doShowAddOptions = useMemo(() => !isEmpty(brochure?.trips
    ?.find((trip) => trip?.id === brochureId && trip?.resultsPageUrl)),
  [brochure, brochureId]);

  const handleSearch = () => {
    if (!brochureId || !brochure?.trips?.length) return;
    const trip = brochure.trips.find((t) => t?.id === brochureId);
    if (!trip?.resultsPageUrl) return;
    const url = new URL(trip.resultsPageUrl, window.location.origin);
    url.searchParams.set('brochureId', brochureId);
    setIsRedirecting(true);
    window.location.href = url.toString();
  };

  const handleCopyLinkClick = async () => {
    let urlString = null;
    if (tripId) {
      urlString = window.location.href;
    } else if (brochureId) {
      urlString = generateBrochureLink(brochureId, travelAgency);
    }
    if (urlString) {
      copyLinkToClipboard(urlString);
      track('resendEmail.copyLink', { url: urlString });
    }
  };

  const handleDeleteTrip = () => {
    const { tripStatus } = tripToDelete;

    if (tripToDelete.tripId) {
      dispatch(deleteTripRequest({
        tripId: tripToDelete.tripId,
        tripType: tripStatus,
      }));
    }

    setTripToDelete(null);
  };

  const onDeleteTrip = () => {
    let tripStatus = tripConfirmation?.tripStatus;
    let tripToDeleteTripId = tripConfirmation?.tripId;
    if (brochureId) {
      tripStatus = BookingAction.BROCHURE;
      tripToDeleteTripId = brochureId;
    } else if (compareId) {
      tripStatus = BookingAction.COMPARE;
      tripToDeleteTripId = compareId;
    }
    setTripToDelete({ tripId: tripToDeleteTripId, tripStatus });
  };

  const showStatus = useCallback((errorMessage) => (
    errorMessage ? (
      <div className="flex gap-1 ml-2 text-right items-center text-red-800" title={errorMessage}>
        <FaExclamationTriangle size="1rem" className="ml-2" />
        {' '}
        Failed!
      </div>
    ) : (
      <div className="flex gap-1 ml-2 text-right items-center text-green-800">
        <FaRegCheckCircle size="1rem" className="ml-2" />
        {' '}
        Sent!
      </div>
    )), []);

  const renderResendEmail = useCallback(({
    doShowUnderline, actionType = ResendEmailTypes.SendClientCopy,
  }) => (
    <a
      onClick={() => handleResendEmail(actionType)}
      onKeyDown={() => { }}
      className={clsx('flex items-center', { underline: doShowUnderline })}
      tabIndex="0"
      role="button"
    >
      Resend email
      {resendEmailStatus === SearchStatus.SEARCHING && <CircularProgress color="secondary" size="1rem" className="ml-1" />}
    </a>
  ), [handleResendEmail, resendEmailStatus]);

  const getDropdownToggleLabel = useMemo(() => {
    if (tripConfirmation?.tripStatus === BookingAction.SEND_QUOTE || brochureId || compareId || tripId) return 'Actions';
    return 'Send Email';
  }, [brochureId, compareId, tripConfirmation, tripId]);

  return (
    <>
      {doRenderDropdown ? (
        <Dropdown className="cursor-pointer self-end">
          <Dropdown.Toggle className="inline-flex justify-center w-full">
            <span className="inline-block align-middle text-base md:text-lg self-center">
              <span>{getDropdownToggleLabel}</span>
            </span>
            <Dropdown.Toggle.Chevron />
          </Dropdown.Toggle>
          <Dropdown.Menu widthClass="w-56">
            <Dropdown.Item>
              {brochureId || compareId ? (
                <button
                  type="button"
                  className={navbarItemClasses}
                  role="menuitem"
                >
                  {renderResendEmail({ actionType: ResendEmailTypes.SendBrochure })}
                </button>
              )
                : (
                  <>
                    <button
                      type="button"
                      onClick={() => handleResendEmail(ResendEmailTypes.SendClientCopy)}
                      className={navbarItemClasses}
                      role="menuitem"
                    >
                      Send Client Copy
                      {resendEmailStatus === SearchStatus.SEARCHING && <CircularProgress color="secondary" size="1rem" className="ml-2" />}
                      {resendEmailStatus === SearchStatus.DONE
                        && showStatus(sendClientCopyEmailError)}
                    </button>
                    {(!isEndCustomer && travelAgency.doSendAgentCopy && isLoggedIn) && (
                      <button
                        type="button"
                        onClick={() => handleResendEmail(ResendEmailTypes.SendAgentCopy)}
                        className={navbarItemClasses}
                        role="menuitem"
                      >
                        Send Agent Copy
                        {sendAgentCopyStatus === SearchStatus.SEARCHING && <CircularProgress color="secondary" size="1rem" className="ml-2" />}
                        {sendAgentCopyStatus === SearchStatus.DONE
                          && showStatus(sendAgentCopyEmailError)}
                      </button>
                    )}
                  </>
                )}
              {cancelledSegments && cancelledSegments.length > 0
                && cancelledSegments.map((segment) => (
                  <button
                    key={segment.tripSegmentId}
                    type="button"
                    onClick={() => handleResendEmail(
                      ResendEmailTypes.SendCancallationEmail, segment.tripSegmentId,
                    )}
                    className={navbarItemClasses}
                    role="menuitem"
                  >
                    Send
                    {' '}
                    {formatTravelMode(segment.mode)}
                    {' '}
                    {cancelledSegments && cancelledSegments.length > 1 && _.filter(cancelledSegments, (c) => c.mode === segment.mode).length > 1 ? `#${segment.tripSegmentId} ` : ''}
                    Cancellation Email
                    {getSendCancellationEmailStatus(segment.tripSegmentId) === SearchStatus.SEARCHING && <CircularProgress color="secondary" size="1rem" className="ml-2" />}
                    {getSendCancellationEmailStatus(segment.tripSegmentId) === SearchStatus.DONE
                      && showStatus(cancellationEmailError)}
                  </button>
                ))}
              {(brochureId || compareId || tripId
                || (tripConfirmation?.tripStatus === BookingAction.SEND_QUOTE)) && (
                  <>
                    {doShowAddOptions
                      && (
                        <button
                          type="button"
                          onClick={handleSearch}
                          className={navbarItemClasses}
                          role="menuitem"
                        >
                          <span className="flex items-center">
                            Add Options
                            {isRedirecting && (
                              <CircularProgress color="secondary" size="1rem" className="ml-2" />
                            )}
                          </span>
                        </button>
                      )}
                    {(brochureId || tripId)
                      && (
                        <button
                          type="button"
                          onClick={handleCopyLinkClick}
                          className={clsx(navbarItemClasses, { 'text-green-700 font-bold': isLinkCopied })}
                          role="menuitem"
                          disabled={isLinkCopied}
                        >
                          {isLinkCopied ? (
                            <span className="flex items-center w-full">
                              <FaRegCheckCircle className="mr-2" />
                              Copied
                            </span>
                          ) : (
                            'Copy Link'
                          )}
                        </button>
                      )}
                    {!tripId && (
                      <button
                        type="button"
                        onClick={onDeleteTrip}
                        className={navbarItemClasses}
                        role="menuitem"
                      >
                        Delete
                      </button>
                    )}
                  </>
              )}
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      ) : (
        renderResendEmail({ doShowUnderline: true })
      )}
      {doShowLoginModal && (
        <LoginRequestModal
          onCancel={() => setDoShowLoginModal(false)}
          actionName="resend email"
        />
      )}
      {tripToDelete && (
        <ConfirmModal
          message={`Are you sure you want to remove the ${formatBookingStatus(tripToDelete.tripStatus).toLowerCase()}?`}
          event="Trips page - remove trip"
          onCancel={() => {
            setTripToDelete(null);
          }}
          onConfirm={handleDeleteTrip}
        />
      )}
      {deleteTripStatus && deleteTripStatus[compareId || brochureId
        || tripConfirmation?.tripId] === SearchStatus.DONE
        && (
          <DeleteConfirmationModal
            tripId={compareId || brochureId || tripConfirmation?.tripId}
            error={deleteTripError}
          />
        )}

      {deleteTripStatus && deleteTripStatus[compareId || brochureId
        || tripConfirmation?.tripId] === SearchStatus.SEARCHING
        && (
          <ModalSpinner tripId={compareId || brochureId || tripConfirmation?.tripId} isOpen size="xs" doHidelogo>
            <div>
              <div className="text-center">
                <CircularProgress color="inherit" />
              </div>
              <br />
              Please wait while we remove the
              {' '}
              {(compareId && 'compare') || (brochureId && 'brochure') || 'trip quote'}
              ...
            </div>
          </ModalSpinner>
        )}
    </>
  );
};
export default ResendEmailDropdown;

ResendEmailDropdown.propTypes = {
  tripId: PropTypes.string,
  brochureId: PropTypes.string,
  compareId: PropTypes.string,
};
ResendEmailDropdown.defaultProps = {
  tripId: undefined,
  brochureId: undefined,
  compareId: undefined,
};
